import { useState, type CSSProperties } from "react";
import type { AdminLogEntry } from "@/server/admin/logEntries";
import { describeLogEntry } from "../utils/describeLogEntry";
import { formatLogEntryJson, formatLogTime, levelColor } from "../utils/logDisplay";

type LogDetailPanelProps = {
  entry: AdminLogEntry;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
};

const ChevronUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M4 10L8 6L12 10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M4 4L12 12M12 4L4 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <rect x="4.5" y="4.5" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.2" />
    <path
      d="M3 9.5V3.5C3 2.67 3.67 2 4.5 2H9.5"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

type DetailRowProps = {
  label: string;
  value: string;
  valueStyle?: CSSProperties;
};

const DetailRow = ({ label, value, valueStyle }: DetailRowProps) => (
  <div className="admin-log-detail-row">
    <span className="admin-log-detail-label">{label}</span>
    <span className="admin-log-detail-value" style={valueStyle}>{value}</span>
  </div>
);

export const LogDetailPanel = ({
  entry,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
}: LogDetailPanelProps) => {
  const [copied, setCopied] = useState(false);
  const descriptionRows = describeLogEntry(entry);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(formatLogEntryJson(entry));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <aside
      className="admin-log-detail"
      role="dialog"
      aria-label="Log details"
    >
      <div className="admin-log-detail-header">
        <button
          type="button"
          className="admin-log-detail-btn"
          onClick={onPrev}
          disabled={!hasPrev}
          aria-label="Previous log entry"
        >
          <ChevronUpIcon />
        </button>
        <button
          type="button"
          className="admin-log-detail-btn"
          onClick={onNext}
          disabled={!hasNext}
          aria-label="Next log entry"
        >
          <ChevronDownIcon />
        </button>
        <button
          type="button"
          className="admin-log-detail-btn"
          onClick={onClose}
          aria-label="Close log details"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="admin-log-detail-fields">
        <DetailRow label="Time" value={formatLogTime(entry.ts)} />
        <DetailRow
          label="Level"
          value={entry.level}
          valueStyle={{ color: levelColor[entry.level], fontWeight: 500 }}
        />
        {entry.runId ? (
          <DetailRow label="Run ID" value={entry.runId} />
        ) : null}
        {entry.phone ? (
          <DetailRow label="Phone" value={entry.phone} />
        ) : null}
        {descriptionRows.map((row) => (
          <DetailRow
            key={row.label}
            label={row.label}
            value={row.value}
            valueStyle={row.label === "Error" ? { color: "var(--log-error)" } : undefined}
          />
        ))}
      </div>

      <div className="admin-log-detail-message-section">
        <div className="admin-log-detail-message-header">
          <span className="admin-log-detail-message-title">Raw message</span>
          <button
            type="button"
            className="admin-log-detail-btn admin-log-detail-copy"
            onClick={() => void handleCopy()}
            aria-label="Copy log entry as JSON"
          >
            <CopyIcon />
            {copied ? <span className="admin-log-detail-copied">Copied</span> : null}
          </button>
        </div>
        <pre className="admin-log-detail-message">{entry.message}</pre>
      </div>
    </aside>
  );
};
